import React, { useMemo } from 'react';
import DiceFace from './DiceFace';
import { AppliedModifier, TriggeredEffect } from '../types';
import { pickPercyReaction, DiceOutcome } from './percyReactions';
import '../styles/DiceResultBreakdown.css';

interface DiceResultBreakdownProps {
  rolls: number[];
  total: number;
  target: number;
  modifiers: AppliedModifier[];
  success: boolean;
  triggeredEffects?: TriggeredEffect[];
}

const DiceResultBreakdown: React.FC<DiceResultBreakdownProps> = ({
  rolls,
  total,
  target,
  modifiers,
  success,
  triggeredEffects = [],
}) => {
  const diceSum = rolls.reduce((a, b) => a + b, 0);
  // Only sum modifiers that were actually applied (condition_met !== false)
  const appliedModifiers = modifiers.filter(m => m.condition_met !== false);
  const modifierSum = appliedModifiers.reduce((a, m) => a + m.value, 0);
  const hasModifiers = modifiers.length > 0;

  // Filter triggered effects to show only "add_card" effects (junk cards on failure)
  const junkCardEffects = triggeredEffects.filter(e => e.effect_type === 'add_card');

  const outcome: DiceOutcome = success ? 'success' : 'failure';
  const percySrc = useMemo(() => pickPercyReaction(outcome), [outcome]);

  return (
    <div className="dice-breakdown">
      {/* Dice sum row */}
      <div className="dice-sum-row">
        {rolls.map((r, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="operator">+</span>}
            <DiceFace value={r} size="medium" />
          </React.Fragment>
        ))}
        {!hasModifiers && (
          <>
            <span className="operator">=</span>
            <span className="sum">{diceSum}</span>
          </>
        )}
      </div>

      {/* Modifiers with condition status */}
      {hasModifiers && (
        <div className="modifiers-section">
          {modifiers.map((mod, i) => {
            const isConditionFailed = mod.condition_met === false;
            const hasCondition = mod.condition_met !== null && mod.condition_met !== undefined;

            return (
              <div
                key={i}
                className={`modifier-row ${isConditionFailed ? 'condition-not-met' : ''}`}
              >
                {hasCondition && (
                  <span className={`condition-indicator ${mod.condition_met ? 'met' : 'not-met'}`}>
                    {mod.condition_met ? '✓' : '✗'}
                  </span>
                )}
                <span className="modifier-value">
                  {mod.value >= 0 ? '+' : ''}{mod.value}
                </span>
                <span className="modifier-source">({mod.card_name})</span>
                {mod.description && (
                  <span className="modifier-description">- {mod.description}</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Equation with final result */}
      {hasModifiers && modifierSum !== 0 && (
        <div className="equation-row">
          <span className="equation-part">{diceSum}</span>
          <span className="operator">{modifierSum >= 0 ? '+' : ''}</span>
          <span className="equation-part">{modifierSum}</span>
          <span className="operator">=</span>
          <span className="total-value">{total}</span>
        </div>
      )}

      {/* Percy's reaction */}
      <img
        className={`percy-reaction ${success ? 'success' : 'failure'}`}
        src={percySrc}
        alt=""
      />

      {/* Final result banner */}
      <div className={`result-row ${success ? 'success' : 'failure'}`}>
        <span className="result-text">{success ? 'SUCCESS!' : 'FAIL!'}</span>
      </div>

      {/* Triggered effects (junk cards added on failure) */}
      {junkCardEffects.length > 0 && (
        <div className="triggered-effects-section">
          {junkCardEffects.map((effect, i) => (
            <div key={i} className="triggered-effect-row">
              <span className="effect-icon">📥</span>
              <span className="effect-text">
                {effect.card_name ? (
                  <>
                    <strong>{effect.card_name.charAt(0).toUpperCase() + effect.card_name.slice(1)}</strong> added to {effect.destination || 'discard'}
                  </>
                ) : (
                  effect.description
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DiceResultBreakdown;
